import { useCore } from "@app/hooks/useCore"
import { useRoutes } from "@app/hooks/useRoutes"
import { useConfigContext } from "@app/providers/config"

import type { Location } from "@root/types/global"

type BreadcrumbProps = {
  data: any
}

// Sourced from the product and bundle page queries, so the tree is only shipped to the routes that use it
type BreadcrumbCollections = GatsbyTypes.TemplateProductQuery["collections"]
type CollectionNode = BreadcrumbCollections["edges"][number]["node"]

export const EXCLUDED_COLLECTION_HANDLES = ["all", "frontpage", "orderlyemails-recommended-products"]

// Merchandising roots. Their children are price bands and seasonal edits, never a product's real category
const EXCLUDED_COLLECTION_ROOT_HANDLES = ["sale", "gifting", "online-warehouse-sale", "online-warehouse-sale-1"]

export const useBreadcrumb = (location?: Location) => {
  const {
    helpers: { capitalise },
  } = useCore()
  const {
    settings: { routes },
  } = useConfigContext()
  const { urlResolver } = useRoutes()
  const breadcrumb = urlResolver({ title: "Home" }, "/home")

  const parentResolver = (parentCollections: Array<CollectionNode>, item: any) => {
    if (item?.parentCollection?.shopify?.handle) {
      parentCollections.unshift(item.parentCollection)
      parentResolver(parentCollections, item.parentCollection)
    }
  }

  const buildBreadcrumbs = ({ data }: BreadcrumbProps): Array<BreadcrumbProps> => {
    const items: Array<any> = [breadcrumb]
    const item = data?.page || data?.product || data?.collection || data?.article || data?.store
    const currentUrl = urlResolver(item)?.url
    const paths = currentUrl?.split("/")?.slice(1)

    if (location?.pathname.includes(routes.PRODUCT) || location?.pathname.includes(routes.BUNDLE)) {
      const collections: BreadcrumbCollections = data?.collections
      const collectionsByHandle = new Map((collections?.edges || []).map(({ node }) => [node?.shopify?.handle, node]))

      const handles: Array<string> = (data?.product?.collections || data?.product?.shopifyCollections || [])
        .map((collection: any) => collection?.handle)
        .filter((handle: string) => handle && !EXCLUDED_COLLECTION_HANDLES.includes(handle))

      let primaryTrail: Array<CollectionNode> = []

      handles.forEach(handle => {
        const collection = collectionsByHandle.get(handle)
        if (!collection) return

        const trail = [collection]
        parentResolver(trail, collection)

        const isNested = trail.length > 1
        const isMerchandising = EXCLUDED_COLLECTION_ROOT_HANDLES.includes(trail[0]?.shopify?.handle || "")

        if (isNested && !isMerchandising && trail.length > primaryTrail.length) primaryTrail = trail
      })

      primaryTrail.forEach(collection =>
        items.push({
          title: collection?.title?.trim(),
          url: urlResolver(collection, routes.COLLECTION)?.url,
        })
      )
    }

    if (item && !location?.pathname.includes(routes.PRODUCT) && !location?.pathname.includes(routes.BUNDLE)) {
      if (data?.collection) {
        const parentCollections = [item]
        parentResolver(parentCollections, item)
        parentCollections.map(collection => {
          urlResolver(collection)
          items.push({
            ...item,
            title: collection?.title?.trim(),
            url: urlResolver(collection).url,
          })
        })
      } else {
        paths?.map(path => {
          if (path === item?.handle?.current || path === item?.shopify?.handle) {
            items.push({
              ...item,
              title: item?.title?.trim(),
              url: urlResolver(item)?.url,
            })
          } else {
            if (path !== "products")
              items.push({
                ...item,
                title: `${capitalise(path?.trim())}`,
                url: `/${path}`,
              })
          }
        })
      }
    }

    return items
  }

  return {
    breadcrumb,
    buildBreadcrumbs,
  }
}
